import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

// Self-hosted and variable: one file covers every weight, and no request leaves
// for a font CDN — which a corporate proxy in front of this app could block or
// slow, dropping the whole interface to a fallback face.
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';

// The cascade, in order: raw scales, then the semantic roles built from them,
// then element defaults, then the few global helpers. Component modules load
// themselves.
import './styles/palette.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/utilities.css';

/*
  A deploy replaces every file of the previous build. A tab opened before it still
  runs the old bundle, and the first lazily loaded screen it visits — the map — asks
  for a chunk that is no longer on the server. Vite reports that here, and a reload
  is the fix: it fetches the new index.html, which names the new files.

  At most once in ten seconds, so a chunk that is genuinely broken surfaces as the
  error it is instead of reloading the page forever. No storage, no reload: without
  the guard there is no telling a loop from a deploy.
*/
const RELOADED_AT = 'reloaded-for-new-build';

window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    return;
  }

  event.preventDefault();
  window.location.reload();
});

const container = document.getElementById('root');

if (!container) {
  // Nothing can recover from this, and a blank page with no explanation is worse
  // than an error naming the missing element.
  throw new Error('Missing #root element in index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
